import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "../../../utils/translation";
import {
  listUsers,
  selectUsers,
  selectUsersError,
  selectUsersLoading,
  selectUsersMessage,
  updateUserBusinessId,
} from "../userSlice";
import {
  listBusinesses,
  selectBusinesses,
  selectBusinessesError,
  selectBusinessesLoading,
  selectBusinessesMessage,
} from "../../business/businessSlice";

const PAGE_SIZE = 10;

const UsersAssignBusinessPage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const users = useSelector(selectUsers);
  const adminAccess = useSelector((state) => state.auth?.adminAccess);
  const businesses = useSelector(selectBusinesses);
  const userLoading = useSelector(selectUsersLoading);
  const userError = useSelector(selectUsersError);
  const userMessage = useSelector(selectUsersMessage);
  const bizLoading = useSelector(selectBusinessesLoading);
  const bizError = useSelector(selectBusinessesError);
  const bizMessage = useSelector(selectBusinessesMessage);
  const [search, setSearch] = useState("");
  const [businessSearch, setBusinessSearch] = useState("");
  const [page, setPage] = useState(1);
  const [permissionError, setPermissionError] = useState("");

  useEffect(() => {
    dispatch(listUsers());
    dispatch(listBusinesses());
  }, [dispatch]);

  const highlightMatch = (text, term) => {
    if (!term) return text;
    const safeText = text || "";
    const lower = safeText.toLowerCase();
    const lowerTerm = term.toLowerCase();
    const index = lower.indexOf(lowerTerm);
    if (index === -1) return safeText || "-";
    const before = safeText.slice(0, index);
    const match = safeText.slice(index, index + term.length);
    const after = safeText.slice(index + term.length);
    return (
      <>
        {before}
        <span style={{ backgroundColor: "yellow" }}>{match}</span>
        {after}
      </>
    );
  };

  const getUserName = (user) => {
    if (user?.fullName) return user.fullName;
    const parts = [user?.name, user?.surName].filter(Boolean);
    return parts.length ? parts.join(" ") : "-";
  };

  const businessMap = useMemo(() => {
    const map = new Map();
    (businesses || []).forEach((biz) => {
      const id = biz.id || biz.businessId;
      if (id) map.set(String(id), biz.businessName || id);
    });
    return map;
  }, [businesses]);

  const filteredBusinesses = useMemo(() => {
    const term = businessSearch.trim().toLowerCase();
    if (!term) return businesses || [];
    return (businesses || []).filter((biz) =>
      (biz?.businessName || "").toLowerCase().includes(term)
    );
  }, [businesses, businessSearch]);

  const businessSuggestions = useMemo(() => {
    if (!businessSearch.trim()) return [];
    return (filteredBusinesses || []).slice(0, 5);
  }, [filteredBusinesses, businessSearch]);

  const filteredUsers = useMemo(() => {
    const term = search.trim().toLowerCase();
    const bizTerm = businessSearch.trim().toLowerCase();
    let result = users || [];
    if (term) {
      result = result.filter((u) => getUserName(u).toLowerCase().includes(term));
    }
    if (bizTerm) {
      result = result.filter((u) => {
        const currentBizName = businessMap.get(String(u.businessId || "")) || "";
        return currentBizName.toLowerCase().includes(bizTerm);
      });
    }
    return result;
  }, [users, search, businessSearch, businessMap]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedUsers = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredUsers.slice(start, start + PAGE_SIZE);
  }, [filteredUsers, currentPage]);

  useEffect(() => {
    if (page !== currentPage) setPage(currentPage);
  }, [page, currentPage]);

  const handleAssign = async (userId, businessId, currentBusinessId) => {
    if (!adminAccess) {
      setPermissionError(t("admin_common.permission_denied"));
      return;
    }
    const id = String(userId);
    const nextBizId = String(businessId || "");
    const currentBizId = String(currentBusinessId || "");
    if (currentBizId && currentBizId !== nextBizId && nextBizId) {
      const confirmed = window.confirm(t("admin_users_pages.assign_confirm_replace"));
      if (!confirmed) return;
    }
    setPermissionError("");
    await dispatch(updateUserBusinessId({ id, businessId: nextBizId }));
    dispatch(listUsers());
  };

  const loading = userLoading || bizLoading;
  const error = userError || bizError;
  const errorMessage = userMessage || bizMessage;

  return (
    <div className="row">
      <div className="col-12">
        <div className="card">
          <div className="card-body">
            <h4 className="card-title">{t("admin_users_pages.assign_title")}</h4>
            {permissionError && (
              <div className="alert alert-danger">{permissionError}</div>
            )}
            {error && (
              <div className="text-danger mb-2">
                {errorMessage || t("admin_common.error_generic")}
              </div>
            )}
            <div className="row mb-3">
              <div className="col-md-6">
                <input
                  type="text"
                  className="form-control"
                  placeholder={t("admin_common.search_name_placeholder")}
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
              <div className="col-md-6">
                <input
                  type="text"
                  className="form-control"
                  placeholder={t("admin_common.search_business_placeholder")}
                  value={businessSearch}
                  onChange={(e) => setBusinessSearch(e.target.value)}
                />
                {businessSuggestions.length > 0 && (
                  <div className="list-group mt-2">
                    {businessSuggestions.map((biz) => {
                      const id = biz.id || biz.businessId;
                      return (
                        <div key={id} className="list-group-item">
                          {highlightMatch(biz.businessName || String(id), businessSearch.trim())}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>{t("admin_common.user")}</th>
                    <th>{t("admin_common.email")}</th>
                    <th>{t("admin_common.current_business")}</th>
                    <th>{t("admin_common.assign_business")}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan="4">{t("admin_common.loading")}</td>
                    </tr>
                  )}
                  {!loading && pagedUsers.length === 0 && (
                    <tr>
                      <td colSpan="4">{t("admin_common.no_records")}</td>
                    </tr>
                  )}
                  {!loading && pagedUsers.map((u) => {
                    const uid = u.id || u.userId;
                    const currentBizName = businessMap.get(String(u.businessId || "")) || "-";
                    return (
                      <tr key={uid}>
                        <td>{highlightMatch(getUserName(u), search.trim())}</td>
                        <td>{u.email || u.userEmail || "-"}</td>
                        <td>{highlightMatch(currentBizName, businessSearch.trim())}</td>
                        <td>
                          <select
                            className="form-select"
                            value={u.businessId || ""}
                            onChange={(e) => handleAssign(uid, e.target.value, u.businessId)}
                          >
                            <option value="">{t("admin_common.select")}</option>
                            {(filteredBusinesses || []).map((biz) => {
                              const id = biz.id || biz.businessId;
                              return (
                                <option key={id} value={id}>
                                  {biz.businessName || id}
                                </option>
                              );
                            })}
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="d-flex align-items-center justify-content-between mt-3">
              <span className="text-muted">
                {t("admin_common.total_records", { count: filteredUsers.length })}
              </span>
              <div className="btn-group">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                >
                  {t("admin_common.prev")}
                </button>
                <button type="button" className="btn btn-outline-secondary" disabled>
                  {currentPage} / {totalPages}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                >
                  {t("admin_common.next")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsersAssignBusinessPage;
